import { addDays, OBSERVATION_KINDS, type ObservationKind } from '@gk/contracts'
import { localDate } from '../lib/time.ts'
import type { LinkRow } from '../photos/usePhotos.ts'
import type { Pending } from '../sync/merge.ts'
import { useObservations, type ObservationRow } from './observations.ts'
import type { Bilingual, JobRow } from './plan.ts'
import type { CoverageRow, JobCompletionRow, WorkSessionRow } from './work.ts'

/** An area's own history (M16-K, benchmark function 13, the area half; docs/M16.md): what was done on a green in the last 90 days, from the device's own
 *  copy — the completions whose coverage names the area (`ops.job_completion` + `ops.job_area_coverage`, mirrored since M8), the people whose timer ran on
 *  that job (`ops.work_session`), the job's settings as the daily sheet prints them, the photos taken on the finish and on the timer (`core.attachment_link`),
 *  and the ground observations recorded on the area (`ops.observation`, M16-N's table in Z's Dexie v13 store). Pure: the page formats, nothing here decides. */

/** The kinds of a ground observation (the contracts' list, M16-N's `ops.observation.kind`; an unknown word is shown as it is), the row as the device holds it and
 *  the hook over the Dexie v13 store `observation`: ONE definition, N's (`data/observations.ts`), re-exported here since Z's close (K's request 2). */
export { OBSERVATION_KINDS, type ObservationKind, type ObservationRow }
export const useAreaObservations = useObservations

/** A photo's link, as the history shows it (the bytes come from this device's copy or from storage: `pages/Course.tsx`). */
export type HistoryPhoto = Pick<LinkRow, 'id' | 'site_id' | 'sha256' | 'mime' | 'caption' | 'captured_at'> & { _pending?: boolean }
type LinkLike = Pick<LinkRow, 'id' | 'site_id' | 'sha256' | 'mime' | 'caption' | 'captured_at' | 'entity_type' | 'entity_id' | 'hidden_at'> & { _pending?: boolean }

export interface AreaWork {
  /** the completion's id */
  id: string
  jobId: string
  /** the site-local day the work was finished */
  date: string
  /** the completion's instant (the list's order) */
  at: string
  taskTypeCode: string
  title: Bilingual | null
  /** the job's settings, formatted by the page with `paramSummary` (the sheet's own words) */
  params: Record<string, string | number>
  /** M25-D: the finish's session, and the settings confirmed at that finish (`ops.job_settings_confirmation`: « fait »), or null (« prévu, non confirmé » where the
   *  device holds every finish, else the plan alone) */
  sessionId: string
  confirmation: { values: Record<string, string>; looks: Record<string, string | null> } | null
  /** what the finish said of THIS area */
  coverage: 'full' | 'partial'
  outcome: 'complete' | 'partial'
  notes: string | null
  /** who finished first, then everyone else whose timer ran on the job (never twice) */
  personIds: string[]
  /** the photos of the finish, then those of the finisher's timer, oldest first */
  photos: HistoryPhoto[]
  pending: boolean
}
export interface AreaObservation {
  id: string
  date: string
  at: string
  kind: string
  note: string | null
  /** the follow-up job, once `observation.link_job@1` named one (rule 1: the link lives on the observation) */
  jobId: string | null
  personId: string | null
  photos: HistoryPhoto[]
  pending: boolean
}
export interface AreaHistory {
  /** the first site-local day shown */
  from: string
  days: number
  /** newest first */
  work: AreaWork[]
  /** newest first */
  observations: AreaObservation[]
}

/** The period an area's page shows: 90 days (docs/M16.md K), printed on the section. */
export const HISTORY_DAYS = 90

type CompletionLike = Pick<JobCompletionRow, 'id' | 'person_id' | 'job_id' | 'work_session_id' | 'outcome' | 'completed_at' | 'notes'> & { _pending?: boolean }
type CoverageLike = Pick<CoverageRow, 'completion_id' | 'area_id' | 'coverage'>
type SessionLike = Pick<WorkSessionRow, 'id' | 'person_id' | 'job_id'>
type JobLike = Pick<JobRow, 'id' | 'task_type_code' | 'title' | 'params'>
type ConfirmationLike = { work_session_id: string; values: Record<string, string>; looks: Record<string, string | null>; confirmed_at: string }
type ObservationLike = Pick<ObservationRow, 'id' | 'area_id' | 'observed_at' | 'note' | 'job_id' | 'actor_person_id'> & { kind: string; _pending?: boolean }

const byInstant = <X extends { at: string; id: string }>(a: X, b: X): number => (a.at < b.at ? 1 : a.at > b.at ? -1 : a.id < b.id ? 1 : a.id > b.id ? -1 : 0)
const photoOrder = (a: LinkLike, b: LinkLike): number => (a.captured_at ?? '').localeCompare(b.captured_at ?? '') || a.id.localeCompare(b.id)
const asPhoto = (l: LinkLike): HistoryPhoto => ({ id: l.id, site_id: l.site_id, sha256: l.sha256, mime: l.mime, caption: l.caption, captured_at: l.captured_at, ...(l._pending ? { _pending: true } : {}) })

/** The work done on one area and the observations made on it, over the last `days` site-local days (today included), newest first. A completion counts when its
 *  coverage names the area (full or partial, as the finish said it for THAT area); a completion whose job the device does not hold yet is left out (its task
 *  and settings are unknown), as the series' markers do. Who: the person who finished, then everyone whose timer ran on the job. Photos: the finish's, then
 *  the finisher's timer's; a hidden link never shows. The window is cut on the site's days (`lib/time.ts`), never on UTC. */
export function areaHistory(
  areaId: string,
  completions: readonly CompletionLike[],
  coverage: readonly CoverageLike[],
  sessions: readonly SessionLike[],
  jobs: readonly JobLike[],
  attachmentLinks: readonly LinkLike[],
  observations: readonly ObservationLike[],
  opts: { tz: string; now?: Date | string | number; days?: number; confirmations?: readonly ConfirmationLike[] },
): AreaHistory {
  const days = opts.days ?? HISTORY_DAYS
  const from = addDays(localDate(opts.now ?? Date.now(), opts.tz), -days)
  const levelOf = new Map<string, 'full' | 'partial'>()
  for (const c of coverage) if (c.area_id === areaId && (c.coverage === 'full' || !levelOf.has(c.completion_id))) levelOf.set(c.completion_id, c.coverage)
  const jobById = new Map(jobs.map((j) => [j.id, j]))
  const sessionById = new Map(sessions.map((s) => [s.id, s]))
  const sessionsOfJob = new Map<string, SessionLike[]>()
  for (const s of sessions) if (s.job_id) sessionsOfJob.set(s.job_id, [...(sessionsOfJob.get(s.job_id) ?? []), s])
  const photosOf = new Map<string, LinkLike[]>()
  for (const l of attachmentLinks) if (!l.hidden_at) photosOf.set(l.entity_id, [...(photosOf.get(l.entity_id) ?? []), l])
  const photos = (entityId: string): HistoryPhoto[] => [...(photosOf.get(entityId) ?? [])].sort(photoOrder).map(asPhoto)

  const confOf = new Map<string, ConfirmationLike>()
  for (const k of opts.confirmations ?? []) { const cur = confOf.get(k.work_session_id); if (!cur || k.confirmed_at > cur.confirmed_at) confOf.set(k.work_session_id, k) }
  const work: AreaWork[] = []
  for (const c of completions) {
    const level = levelOf.get(c.id)
    if (!level) continue
    const date = localDate(c.completed_at, opts.tz)
    if (date < from) continue
    const job = jobById.get(c.job_id)
    if (!job) continue
    const own = sessionById.get(c.work_session_id)
    const personIds = [...new Set([c.person_id, ...(own ? [own.person_id] : []), ...(sessionsOfJob.get(c.job_id) ?? []).map((s) => s.person_id)])]
    work.push({
      id: c.id, jobId: c.job_id, date, at: c.completed_at, taskTypeCode: job.task_type_code, title: job.title, params: job.params, sessionId: c.work_session_id, confirmation: confOf.has(c.work_session_id) ? { values: confOf.get(c.work_session_id)!.values, looks: confOf.get(c.work_session_id)!.looks } : null, coverage: level, outcome: c.outcome,
      notes: c.notes, personIds, photos: [...photos(c.id), ...(own ? photos(own.id) : [])], pending: !!c._pending,
    })
  }
  const obs: AreaObservation[] = []
  for (const o of observations) {
    if (o.area_id !== areaId) continue
    const date = localDate(o.observed_at, opts.tz)
    if (date < from) continue
    obs.push({ id: o.id, date, at: o.observed_at, kind: o.kind, note: o.note, jobId: o.job_id, personId: o.actor_person_id ?? null, photos: photos(o.id), pending: !!o._pending })
  }
  return { from, days, work: work.sort(byInstant), observations: obs.sort(byInstant) }
}
